<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-procedures'"
      :section-title="$tc('configuration.sections.regimes', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-testing="'activate'"
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'deactivate'"
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-testing="'delete'"
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteRegimes(selectedRows)"
        @shortkey="deleteRegimes(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'update'"
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showRegimeModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showRegimeModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-testing="'add'"
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showRegimeModal($t('configuration.actions.add'))"
        @shortkey="showRegimeModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="regimeModal">
      <regime-modal
        :action-title="actionTitle"
        @update="patchRegimes($event)"
        @create="createRegime($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsRegimesGrid from '@/modules/configurationModule/detail/cases/regimes/columnsRegimesGrid'
import RegimeModal from '@/modules/configurationModule/detail/cases/regimes/RegimeModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import RegimeConfigurationServices from '@/api/resources/configuration/cases/regime/RegimeConfigurationServices'

export default {
    name: 'CaseRegimesGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        RegimeModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.cases.regimes',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsRegimesGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveRegime() {
            return !!this.selectedRows.find(regime => regime.active)
        },
        hasDeactiveRegime() {
            return !!this.selectedRows.find(regime => !regime.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(regime => regime.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(regime => !regime.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveRegime && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveRegime && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const regimesToPatch =
                selectionBatch
                    .filter(regime => regime.active !== option)
                    .map(regime => ({
                        active: option,
                        id: regime.id
                    }))
            if (regimesToPatch.length) {
                const response =
                    await this.$_toCallUnique(
                        RegimeConfigurationServices.patchRegimesBatch(regimesToPatch))
                if (response !== null) {
                    this.$_showToastSuccess(
                        this.$tc(
                            'configuration.toastMessages.updated',
                            regimesToPatch.length,
                            { elements: regimesToPatch.length }))
                    this.reloadData()
                }
            }
        },
        async patchRegimes(...regimes) {
            const regimePatchInBatchRequests =
                regimes.map(regime => ({
                    active: regime.active,
                    code: regime.code,
                    id: regime.id,
                    nameTranslations: regime.nameTranslations.map(nameTranslation => ({
                        languageId: nameTranslation.language.id,
                        notes: nameTranslation.notes,
                        value: nameTranslation.value
                    }))
                }))

            const response =
                await this.$_toCallUnique(
                    RegimeConfigurationServices.patchRegimesBatch(regimePatchInBatchRequests))
            if (response !== null) {
                this.$refs.regimeModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                this.reloadData()
            }
        },
        async createRegime(regime) {
            const regimeCreateRequest = {
                active: regime.active,
                code: regime.code,
                nameTranslations: regime.nameTranslations.map(nameTranslation => ({
                    languageId: nameTranslation.language.id,
                    notes: nameTranslation.notes,
                    value: nameTranslation.value
                }))
            }
            const response =
                await this.$_toCallUnique(
                    RegimeConfigurationServices.createRegime(regimeCreateRequest))
            if (response !== null) {
                this.$refs.regimeModal.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deleteRegimesBatch(regimes) {
            const regimesToDelete = regimes.map(regime => regime.id)
            const response =
                await this.$_toCallUnique(
                    RegimeConfigurationServices.deleteRegimesBatch(regimesToDelete))
            if (response !== null) {
                this.$_showToastSuccess(
                    this.$tc(
                        'configuration.toastMessages.deleted',
                        regimesToDelete.length,
                        { elements: regimesToDelete.length }))
                this.reloadData()
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(RegimeConfigurationServices))
        },
        getContextMenuItems(params) {
            if (params?.node) {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showRegimeModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteRegimes(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
            return []
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(RegimeConfigurationServices))
        },
        showRegimeModal(title, selectedRegime) {
            this.actionTitle = title
            this.$refs.regimeModal.show([selectedRegime])
        },
        async deleteRegimes(regimes) {
            if (regimes.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteRegimesBatch(regimes)
                }
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
